import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApiService } from '../../services/api/api.service';
import { environment } from '../../../environments/environment';

export interface ValuationDetail {
  id: number;
  valuationNumber: string;
  assignedPrice: number;
  dgwAmount: number;
  margin: number;
  marginActual: number;
  created: string;
  statusType: string;
  vehicleInfo?: string;
  marginDate: string | null;
}

@Component({
  selector: 'app-margin-details-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './margin-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./margin-details-modal.component.scss']
})
export class MarginDetailsModalComponent implements OnInit, OnDestroy {
  public isVisible = false;
  public selectedDate: string = '';
  public valuations: ValuationDetail[] = [];
  public loading = false;
  public error: string | null = null;

  private destroy$ = new Subject<void>();

  constructor(private apiService: ApiService) {}

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Open the modal and load valuations for the selected date
   */
  public open(date: string): void {
    this.selectedDate = date;
    this.isVisible = true;
    this.loadValuations(date);
  }

  /**
   * Close the modal
   */
  public close(): void {
    this.isVisible = false;
    this.valuations = [];
    this.error = null;
  }

  /**
   * Load valuation details for the selected date
   */
  public loadValuations(date: string): void {
    this.loading = true;
    this.error = null;

    // Query for valuations with marginDate on this date
    const query = JSON.stringify({
      size: 100,
      query: {
        wildcard: {
          "Properties.marginDate.keyword": `${date}*`
        }
      },
      sort: [
        { "Properties.created": { order: "desc" } }
      ]
    });

    this.apiService.statisticsSearch(environment.elasticIndexName, query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          const hits = result.data.hits?.hits || [];

          this.valuations = hits
            .map((hit: any) => {
              const source = hit._source;
              const vehicle = source.VehicleSpecification?.Vehicle;
              const marginActual = source.Properties.marginActual || 0;
              const marginDate = source.Properties.marginDate || null;

              return {
                id: source.Properties.id,
                valuationNumber: source.Properties.id?.toString() || 'N/A',
                assignedPrice: source.Properties.assignedPrice || 0,
                dgwAmount: source.Properties.dgwAmount || 0,
                margin: source.Properties.margin || 0,
                marginActual: marginActual,
                marginDate: marginDate,
                created: source.Properties.created || '',
                statusType: source.Properties.statusType || 'N/A',
                vehicleInfo: vehicle ? `${vehicle.make} ${vehicle.model} (${vehicle.registration || 'N/A'})` : 'N/A'
              };
            })
            .filter(v => v.marginActual !== 0);

          // Sort by margin (highest first) so most relevant are at top
          this.valuations.sort((a, b) => b.marginActual - a.marginActual);

          this.loading = false;
        },
        error: (err) => {
          this.error = 'Failed to load valuation details';
          this.loading = false;
        }
      });
  }

  /**
   * Format a number as currency (EUR)
   */
  public formatCurrency(value: number): string {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  }

  /**
   * Format margin number without decimals (e.g., € 1.039 instead of € 1.039,00)
   */
  public formatMargin(value: number): string {
    return '€ ' + new Intl.NumberFormat('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }

  /**
   * Get CSS class based on margin value
   */
  public getMarginClass(margin: number): string {
    if (margin > 500) return 'margin-high';
    if (margin > 0) return 'margin-positive';
    if (margin < 0) return 'margin-negative';
    return 'margin-zero';
  }

  /**
   * Track valuations by ID for performance
   */
  public trackByValuationId(index: number, valuation: ValuationDetail): number {
    return valuation.id;
  }

  /**
   * Open valuation detail in new tab
   */
  public openValuationDetail(id: number): void {
    window.open(`https://admin.autotaxatiepartners.nl/taxatie/detail/${id}`, '_blank');
  }
}
